import type { RouterHistory } from 'vue-router';
import eventBus from '~/event_bus';
import { useModalHistory } from '~/composable/use_modal_history';

function closeTop() {
    eventBus.emit('close');
}

export function withModalBack(routerHistory: RouterHistory): RouterHistory {
    const base = { ...routerHistory };
    const modals = useModalHistory();
    const skip = ref(0);
    let depth = 0;

    function whenSettled(fn: () => void) {
        if (skip.value > 0) {
            void until(skip).toBe(0).then(fn);
        } else {
            fn();
        }
    }

    function unwind(target: number) {
        if (depth <= target) return;
        skip.value++;
        window.history.go(target - depth);
        depth = target;
    }

    function sync() {
        if (skip.value > 0) {
            whenSettled(sync);
            return;
        }

        while (depth < modals.value.length) {
            window.history.pushState({ ...window.history.state, modal: ++depth }, '');
        }

        unwind(modals.value.length);
    }

    function navigate(fn: () => void) {
        unwind(0);
        whenSettled(() => {
            fn();
            sync();
        });
    }

    function consumePopState() {
        if (skip.value > 0) {
            skip.value--;
            return true;
        }

        const target: number = window.history.state?.modal ?? 0;
        if (target >= depth) return false;

        while (depth > target + 1) {
            depth--;
            closeTop();
        }

        depth--;
        void Promise.resolve((modals.value[0].onBack ?? closeTop)()).then(sync);
        return true;
    }

    if (window.history.state?.modal) {
        window.history.replaceState({ ...window.history.state, modal: 0 }, '');
    }

    watch(() => modals.value.length, sync, { flush: 'sync' });

    return Object.assign(routerHistory, {
        listen: (callback) => base.listen((...args) => {
            if (!consumePopState()) callback(...args);
        }),
        go: (delta, triggerListeners) => whenSettled(() => base.go(delta, triggerListeners)),
        push: (to, data) => navigate(() => base.push(to, data)),
        replace: (to, data) => navigate(() => base.replace(to, data)),
    } satisfies Partial<RouterHistory>);
}
